import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db';
import { toast } from '@/stores/toastStore';
import { useRows } from '@/hooks/useLive';
import { useAuthStore } from '@/stores/authStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { CheckboxInput, NumberInput, SelectInput } from '@/components/ui/Field';
import type { Setting } from '@/types/models';

const CATEGORY_ORDER = ['production', 'concrete', 'fleet', 'fuel', 'blast', 'maintenance', 'validation', 'general', 'system'];

const CATEGORY_TITLE: Record<string, string> = {
  production: 'Production',
  concrete: 'Concrete branches — company defaults',
  fleet: 'Fleet — availability & utilisation',
  fuel: 'Fuel — consumption report',
  blast: 'Blasting & depletion forecast',
  maintenance: 'Maintenance',
  validation: 'Data validation',
  general: 'General',
  system: 'System',
};

const CATEGORY_NOTE: Record<string, string> = {
  production: 'The quarry\'s. The working week and the month-to-date target are also the default for the concrete branches, which can set their own in Concrete → Branch settings.',
  concrete: 'Each branch follows these unless it sets its own in Concrete → Branch settings.',
  fleet: 'These define what availability and utilisation mean. Change them and every dashboard, drill-down and report changes together — they all read the same calculation.',
  fuel: 'When a fill\'s consumption is flagged high or low against the vehicle\'s own average for the period (Fuel → Consumption).',
  blast: 'The averaging window and the thresholds behind the green / amber / red blast warning.',
  validation: 'How strict capture is. Hard rules block the entry; everything else is surfaced as a data-quality warning instead.',
};

/** Text settings that are really a choice. */
const CHOICES: Record<string, { value: string; label: string }[]> = {
  utilisation_base: [
    { value: 'available', label: 'Available hours (recommended)' },
    { value: 'scheduled', label: 'Scheduled shift hours' },
  ],
  mtd_target_basis: [
    { value: 'pro_rata', label: 'Pro rata to date (recommended)' },
    { value: 'full_month', label: "Whole month's target" },
  ],
  working_week: [
    { value: 'mon_fri', label: 'Monday to Friday' },
    { value: 'mon_sat', label: 'Monday to Saturday' },
    { value: 'mon_sun', label: 'Every day' },
  ],
};

export function SettingsPage() {
  const rows = useRows<Setting>('settings');
  const isAdmin = useAuthStore((s) => s.hasRole('manager', 'admin'));
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!rows) return;
    setDraft((d) => {
      const next = { ...d };
      for (const r of rows) if (!(r.key in next)) next[r.key] = r.value;
      return next;
    });
  }, [rows]);

  async function persist(setting: Setting) {
    setSaving(setting.key);
    const value = draft[setting.key];
    const { data, error } = await supabase
      .from('settings')
      .update({ value })
      .eq('key', setting.key)
      .select()
      .single();
    setSaving(null);

    if (error) {
      toast.error(
        error.code === '42501' || error.message.includes('row-level security')
          ? 'Your role does not allow changing settings.'
          : error.message,
      );
      return;
    }
    await db.settings.put(data as Setting);
    toast.success(`${setting.label} updated`);
  }

  if (!rows) {
    return <div className="centered"><span className="spinner" /></div>;
  }

  const byCategory = new Map<string, Setting[]>();
  for (const r of rows) {
    // Mail configuration has its own screen, with the recipients, the test
    // send and the warning about where the password belongs.
    if (r.category === 'notification') continue;
    // Bookkeeping the system keeps for itself (the data epoch), not a
    // setting anyone should change.
    if (r.category === 'internal') continue;
    const list = byCategory.get(r.category) ?? [];
    list.push(r);
    byCategory.set(r.category, list);
  }

  const categories = [...byCategory.keys()].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
  );

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      {!isAdmin && (
        <div className="small muted">
          Settings are read only for your role. A manager or administrator can change them.
        </div>
      )}

      {categories.map((cat) => (
        <Panel key={cat} title={CATEGORY_TITLE[cat] ?? cat}>
          {CATEGORY_NOTE[cat] && (
            <p className="small muted" style={{ marginTop: 0 }}>{CATEGORY_NOTE[cat]}</p>
          )}

          {(byCategory.get(cat) ?? [])
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((s) => {
              const dirty = JSON.stringify(draft[s.key]) !== JSON.stringify(s.value);
              return (
                <div className="setting" key={s.key}>
                  <div>
                    <div className="setting__label">{s.label}</div>
                    {s.description && <div className="setting__desc">{s.description}</div>}
                    <div className="setting__key">{s.key}</div>
                  </div>

                  <div className="col" style={{ gap: 'var(--sp-2)' }}>
                    <SettingControl
                      setting={s}
                      value={draft[s.key]}
                      disabled={!isAdmin}
                      onChange={(v) => setDraft((d) => ({ ...d, [s.key]: v }))}
                    />
                    {dirty && isAdmin && (
                      <div className="row" style={{ gap: 'var(--sp-2)' }}>
                        <Button
                          style={{ minHeight: 32 }}
                          loading={saving === s.key}
                          onClick={() => void persist(s)}
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          style={{ minHeight: 32 }}
                          onClick={() => setDraft((d) => ({ ...d, [s.key]: s.value }))}
                        >
                          Reset
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </Panel>
      ))}
    </div>
  );
}

function SettingControl({
  setting, value, disabled, onChange,
}: { setting: Setting; value: unknown; disabled: boolean; onChange: (v: unknown) => void }) {
  const id = `s-${setting.key}`;

  if (setting.data_type === 'boolean') {
    return (
      <CheckboxInput
        id={id}
        label={value ? 'Enabled' : 'Disabled'}
        value={Boolean(value)}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  if (setting.data_type === 'number') {
    return (
      <NumberInput
        id={id}
        label=""
        value={value === null || value === undefined ? null : Number(value)}
        disabled={disabled}
        onChange={(v) => onChange(v)}
      />
    );
  }

  const choices = CHOICES[setting.key];
  if (choices) {
    return (
      <SelectInput
        id={id}
        label=""
        value={value == null ? null : String(value)}
        options={choices}
        disabled={disabled}
        placeholder="— select —"
        onChange={(v) => onChange(v)}
      />
    );
  }

  return (
    <div className="field">
      <input
        id={id}
        value={value == null ? '' : String(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
